/**
 * measureGeo.ts — حساب المسافة والمساحة على سطح الكرة (أدوات القياس بأسلوب Zoom Earth).
 *
 * كل الحسابات كرويّة لا مستوية: على شاشة مركاتور تبدو الجزائر بحجم أوروبا، فقياس
 * المساحة بمعادلة المضلّع المستوي على إحداثيات الشاشة كان يعطي رقماً يكبر كلّما
 * ابتعد المضلّع عن خط الاستواء — والمستخدم لا يرى سبباً لذلك. الصيغ هنا تعمل على
 * خطوط الطول/العرض مباشرةً فتُعطي الرقم نفسه أينما رُسم الشكل.
 */

export interface LngLat { lng: number; lat: number }

/** نصف قطر الأرض المتوسّط (IUGG mean radius) بالأمتار. */
const EARTH_RADIUS_M = 6_371_008.8;

const toRad = (deg: number) => (deg * Math.PI) / 180;

/** المسافة الكبرى (great-circle) بين نقطتين بالأمتار — صيغة haversine. */
export function haversineMeters(a: LngLat, b: LngLat): number {
    const φ1 = toRad(a.lat), φ2 = toRad(b.lat);
    const dφ = φ2 - φ1;
    const dλ = toRad(b.lng - a.lng);
    const h = Math.sin(dφ / 2) ** 2 + Math.cos(φ1) * Math.cos(φ2) * Math.sin(dλ / 2) ** 2;
    return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** مجموع أطوال أضلاع مسار مفتوح، بالأمتار. */
export function pathLengthMeters(points: LngLat[]): number {
    let total = 0;
    for (let i = 1; i < points.length; i++) total += haversineMeters(points[i - 1], points[i]);
    return total;
}

/**
 * مساحة مضلّع كروي بالأمتار المربّعة — تكامل خطّي على الأضلاع (صيغة المساحة
 * المُوقَّعة المستعملة في PostGIS/turf). يُغلق المضلّع تلقائياً بين آخر نقطة وأولاها.
 */
export function sphericalAreaM2(points: LngLat[]): number {
    if (points.length < 3) return 0;
    let sum = 0;
    for (let i = 0; i < points.length; i++) {
        const p1 = points[i];
        const p2 = points[(i + 1) % points.length];
        sum += toRad(p2.lng - p1.lng) * (2 + Math.sin(toRad(p1.lat)) + Math.sin(toRad(p2.lat)));
    }
    return Math.abs((sum * EARTH_RADIUS_M * EARTH_RADIUS_M) / 2);
}

/**
 * تقسيم ضلع إلى نقاط على القوس الأكبر. الخطّ المستقيم على الشاشة ليس أقصر مسار
 * على الكرة: ضلعٌ من المغرب إلى الصين يُرسم مستقيماً بينما الرقم المكتوب عليه هو
 * طول القوس — فيقرأ المستخدم مسافةً لا تطابق ما يراه. التقسيم يجعل الرسم يتبع
 * الرقم. الأضلاع القصيرة تُترك كما هي (الفرق دون البكسل).
 */
export function greatCircleSegment(a: LngLat, b: LngLat, steps = 48): LngLat[] {
    const d = haversineMeters(a, b);
    if (d < 200_000) return [a, b];

    const δ = d / EARTH_RADIUS_M;
    const sinδ = Math.sin(δ);
    const φ1 = toRad(a.lat), λ1 = toRad(a.lng);
    const φ2 = toRad(b.lat), λ2 = toRad(b.lng);
    const out: LngLat[] = [];

    for (let i = 0; i <= steps; i++) {
        const f = i / steps;
        const A = Math.sin((1 - f) * δ) / sinδ;
        const B = Math.sin(f * δ) / sinδ;
        const x = A * Math.cos(φ1) * Math.cos(λ1) + B * Math.cos(φ2) * Math.cos(λ2);
        const y = A * Math.cos(φ1) * Math.sin(λ1) + B * Math.cos(φ2) * Math.sin(λ2);
        const z = A * Math.sin(φ1) + B * Math.sin(φ2);
        out.push({
            lat: (Math.atan2(z, Math.hypot(x, y)) * 180) / Math.PI,
            lng: (Math.atan2(y, x) * 180) / Math.PI,
        });
    }
    return out;
}

/** كثّف مساراً كاملاً بأقواس كبرى بين كل نقطتين متتاليتين. */
export function densifyPath(points: LngLat[]): LngLat[] {
    if (points.length < 2) return points;
    const out: LngLat[] = [points[0]];
    for (let i = 1; i < points.length; i++) {
        out.push(...greatCircleSegment(points[i - 1], points[i]).slice(1));
    }
    return out;
}

// ── تنسيق الأرقام ────────────────────────────────────────────────────────────

const MILE_M = 1609.344;

/** رقمٌ بفواصل آلاف ومنازل عشرية متناسبة مع حجمه (لا 3٫000000 ولا 3550٫47). */
function fmt(value: number, locale: string): string {
    const digits = value >= 100 ? 0 : value >= 10 ? 1 : 2;
    return value.toLocaleString(locale, { maximumFractionDigits: digits, minimumFractionDigits: 0 });
}

/** مسافة مقروءة: أمتار/أقدام تحت الكيلومتر الواحد، ثم كم/ميل. */
export function formatDistance(meters: number, imperial: boolean, locale: string): string {
    if (imperial) {
        const miles = meters / MILE_M;
        if (miles < 0.5) return `${fmt(meters * 3.280839895, locale)} ft`;
        return `${fmt(miles, locale)} mi`;
    }
    if (meters < 1000) return `${fmt(meters, locale)} m`;
    return `${fmt(meters / 1000, locale)} km`;
}

/** مساحة مقروءة — نفس تدرّج Zoom Earth: كم² (أو ميل²) مع فواصل الآلاف. */
export function formatArea(m2: number, imperial: boolean, locale: string): string {
    if (imperial) {
        const mi2 = m2 / (MILE_M * MILE_M);
        if (mi2 < 0.1) return `${fmt(m2 * 10.763910417, locale)} ft²`;
        return `${fmt(mi2, locale)} mi²`;
    }
    if (m2 < 100_000) return `${fmt(m2, locale)} m²`;
    return `${fmt(m2 / 1_000_000, locale)} km²`;
}
